import React, { useState, useEffect, useRef } from "react";
import { LoadingContainer, ScreenResponsive } from "../../components";
import { Grid, GridItem, useScreenSize } from "@telefonica/mistica";
import { observer } from "mobx-react-lite";
import { strings } from "../../localization";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import { CONNECT_FGTS_CALLS_DELAY, CONNECT_FGTS_TIMEOUT } from "../../constants";
import { apiTypes } from "../../services";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useFetchBalance, useNavigateCustom } from "../../hooks";

const ConnectFGTSBalance: React.FC = observer(() => {
	const { isMobile } = useScreenSize();
	const navigate = useNavigateCustom();
	const { getFetchBalance } = useFetchBalance();
	const getBalanceRef = useRef(true);
	const { anticipationStore } = useGlobalStore();
	const { addAnalyticEvent, updatePageView } = useAnalytics();

	const { connectFGTSBalance: { loadingContainerTitle }, navigation: { anticipation } } = strings.pages;
	const [elapsedTime, setElapsedTime] = useState<number>(0);

	// Função para determinar qual opção passar para loadingContainerTitle
	const getLoadingTitleOption = (): number => {
		if (elapsedTime < 40) {
			return 1; // Até 40 segundos
		} else if (elapsedTime < 60) {
			return 2; // Entre 40 segundos e 1 minuto
		} else {
			return 3; // Acima de 1 minuto
		}
	};

	// Adicionar um useEffect para incrementar o contador a cada segundo
	useEffect(() => {
		const timer = setInterval(() => {
			setElapsedTime((prevTime) => prevTime + 1);
		}, 1000);

		// Limpar o intervalo quando o componente for desmontado
		return () => {
			clearInterval(timer);
			setElapsedTime(0); // Reiniciar o contador quando sair da tela
		};
	}, []);

	const onSuccess = async () => {
		// Chama a função de verificação de elegibilidade do cliente
		const isEligible = await anticipationStore.onVerifyEligible();

		if (!isEligible) {
			return;
		}

		navigate("/anticipation", { replace: true });
	};

	const onFailed = React.useCallback(
		(status: apiTypes.BalanceInquiryRequestErrorEnum) => {
			switch (status) {
				case apiTypes.BalanceInquiryRequestErrorEnum.ONGOING_OPERATION:
					return navigate("/ongoing-operation");
				case apiTypes.BalanceInquiryRequestErrorEnum.UNAUTHORIZED_INSTITUTION:
					return navigate("/unauthorized-institution");
				case apiTypes.BalanceInquiryRequestErrorEnum.INEXISTENT_ANNIVERSARY_MEMBERSHIP:
					return navigate("/inexistent-anniversary-membership");
				case apiTypes.BalanceInquiryRequestErrorEnum.ON_LOCKED_DATE_RANGE:
					return navigate("/on-locked-data-range");
				case apiTypes.BalanceInquiryRequestErrorEnum.ANNIVERSARY_MEMBERSHIP_EGRESS:
					return navigate("/anniversary-membership-egress");
				case apiTypes.BalanceInquiryRequestErrorEnum.PROCESSING_PENDING_CHANGES:
					return navigate("/processing-pending-changes");
				case apiTypes.BalanceInquiryRequestErrorEnum.CAIXA_ERROR:
					return navigate("/caixa-error");
				case apiTypes.BalanceInquiryRequestErrorEnum.LACK_PERIOD:
					return navigate("/lack-period");
				case apiTypes.BalanceInquiryRequestErrorEnum.ONLY_ONE_OPERATION_ALLOWED_ANNUALLY:
					return navigate("/one-operation-allowed-anually");
			}
		},
		[],
	);

	React.useEffect(
		() => {
			updatePageView({
				name: "Vivo Money FGTS Simulador Quente Conectando Conta",
				pagePath: "/fgts/simulador-quente/conectando",
			});

			const interval = setInterval(
				(): void => {
					handleGetBalance();
				},
				CONNECT_FGTS_CALLS_DELAY,
			);

			return () => clearInterval(interval);
		},
		[],
	);

	const onBack = (): void => {
		addAnalyticEvent({
			action: "click.button",
			component: "header",
			description: anticipation,
			step: "fgts:simulador-quente:conectando",
			value: "",
		}, { applySanitize: true }); navigate(-1);
	};

	useEffect(() => {
		const timeout = setTimeout(
			(): void => {
				navigate("/unavailable-real-time");
			},
			CONNECT_FGTS_TIMEOUT,
		);

		return () => clearTimeout(timeout);
	}, []);

	const handleGetBalance = async () => {
		await getFetchBalance({onFailed, onSuccess});
	};

	useEffect(() => {
		if (getBalanceRef.current) {
			getBalanceRef.current = false;
			handleGetBalance();
		}
	}, []);

	return (
		<ScreenResponsive navigationTitle={anticipation} onBack={onBack}>
			<Grid justifyContent="center">
				<Grid height={isMobile ? 488 : 600} alignItems="center">
					<GridItem>
						<LoadingContainer title={loadingContainerTitle(getLoadingTitleOption())} />
					</GridItem>
				</Grid>
			</Grid>
		</ScreenResponsive>
	);
});

export default ConnectFGTSBalance;
